import { useEffect, useMemo, useState } from "react";
import { ApiService } from "../../../../services/api";
import { Trash2, Clock, BookOpen } from "lucide-react";
import checkPermission from "../../../../utils/permission";
import ConfirmModal from "../../../../components/modals/ConfirmModal";

type LessonComment = {
  id: string;
  lessonId: string;
  sectionId?: string | null;
  userId: string;
  content: string;
  metadata?: any;
  createdAt?: string;
  user?: { firstName?: string; lastName?: string };
};

export default function LessonNotesView({
  lessonId,
  sections,
  defaultSectionId,
  fixedScope,
}: {
  lessonId: string;
  sections: any[];
  defaultSectionId?: string;
  fixedScope?: { type: "lesson" } | { type: "section"; sectionId: string };
}) {
  const canList = checkPermission("lesson_note_list");
  const canAdd = checkPermission("lesson_note_add");
  const canDelete = checkPermission("lesson_note_delete");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LessonComment[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | "lesson">(defaultSectionId ? defaultSectionId : "lesson");
  const [newNote, setNewNote] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const sectionMap = useMemo(() => {
    const map: Record<string, string> = {};
    (sections || []).forEach((s: any) => (map[s.id] = s.title));
    return map;
  }, [sections]);

  useEffect(() => {
    fetchNotes();
  }, [lessonId, selectedSectionId, fixedScope?.type, (fixedScope as any)?.sectionId]);

  const fetchNotes = async () => {
    if (!canList) return;
    setLoading(true);
    try {
      const res = await ApiService.getLessonNotes(lessonId, {
        limit: 200,
        sortBy: "createdAt",
        sortOrder: "DESC",
      });
      const data: LessonComment[] = Array.isArray(res) ? res : res?.data || [];
      setItems(data);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!canAdd || !newNote.trim()) return;
    setLoading(true);
    try {
      await ApiService.createLessonNote(lessonId, {
        content: newNote.trim(),
        sectionId:
          fixedScope?.type === "lesson"
            ? undefined
            : fixedScope?.type === "section"
            ? fixedScope.sectionId
            : selectedSectionId && selectedSectionId !== "lesson"
            ? (selectedSectionId as string)
            : undefined,
      });
      setNewNote("");
      await fetchNotes();
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (noteId: string) => {
    if (!canDelete) return;
    setDeleteId(noteId);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteId(null);
    setDeleteLoading(false);
  };

  const confirmDelete = async () => {
    if (!canDelete || !deleteId) return;
    setDeleteLoading(true);
    try {
      await ApiService.deleteLessonNote(lessonId, deleteId);
      await fetchNotes();
      closeDelete();
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!canList) {
    return <div className="text-center text-gray-600">Bu alanı görüntüleme yetkiniz yok.</div>;
  }

  const activeSectionId =
    fixedScope?.type === "section"
      ? fixedScope.sectionId
      : selectedSectionId && selectedSectionId !== "lesson"
      ? (selectedSectionId as string)
      : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-purple-600 flex-shrink-0" />
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">Notlar</h2>
        {activeSectionId && (
          <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
            {sections?.find((s: any) => s.id === activeSectionId)?.title || "Bölüm"}
          </span>
        )}
      </div>

      <div>
        <textarea
          className="w-full border rounded-lg px-3 py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-200 min-h-[72px]"
          placeholder={
            activeSectionId
              ? `${sections?.find((s: any) => s.id === activeSectionId)?.title} bölümü için notunuzu yazın...`
              : "Bu bölüm için notunuzu yazın..."
          }
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          disabled={loading}
        />
        <div className="mt-2 flex justify-end">
          <button
            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50"
            onClick={handleCreate}
            disabled={loading || !newNote.trim()}>
            Kaydet
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-sm sm:text-base text-gray-500">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-6 sm:p-8 text-center shadow-sm">
          <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
            {activeSectionId ? "Bu Bölüm İçin Henüz Not Eklenmemiş" : "Henüz Not Eklenmemiş"}
          </h3>
          <p className="text-sm sm:text-base text-gray-500">
            {activeSectionId ? "İlk notunuzu ekleyerek öğrenmeye başlayın." : "Öğrenme deneyiminizi geliştirmek için notlarınızı tutun."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n) => (
            <div
              key={n.id}
              className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-50 hover:border-purple-300 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1 space-y-2 min-w-0">
                  {n.sectionId && (
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700 font-medium truncate">
                        {sections?.find((s: any) => s.id === n.sectionId)?.title || "Bölüm"}
                      </span>
                    </div>
                  )}

                  <div className="text-sm sm:text-base text-gray-900 whitespace-pre-wrap leading-relaxed">{n.content}</div>

                  {n.createdAt && (
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-gray-600">{new Date(n.createdAt).toLocaleString("tr-TR")}</span>
                    </div>
                  )}
                </div>

                <div className="flex-shrink-0 self-start sm:self-auto">
                  <button
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                    onClick={() => openDelete(n.id)}
                    disabled={loading}>
                    <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                    Sil
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        title="Notu Sil"
        message="Bu notu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        isLoading={deleteLoading}
      />
    </div>
  );
}
